import { FormHelperText } from "@mui/material";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import moment from "moment";
import { useSelector } from "react-redux";
import { StyledCommonForms } from "../../../styled/form";
import Fpt from "../fpt/fpt";
import {
  catalogSelectMenuItems,
  mapCatalogToOptions,
} from "./catalogMenuItems.jsx";
import {
  getFormFieldOptions,
  getIdEstadoInicialTraslados,
  isModalidadRecorridoLocked,
  ORIGENES_PEDIDO,
  recorridoSoloLecturaPrestador,
  validatePuntosForm,
} from "./fptTrasladosUtils";
import ModalidadRetornoSync from "./modalidadRetornoSync";
import RecorridoList from "./recorridoList";
import TramoGestionDefaults from "./tramoGestionDefaults";
import TramosAGenerarResumen from "./tramosAGenerarResumen";
import TramosGestionList from "./tramosGestionList";
import { FormikSelect } from "@accessinformatica/formik-mui";
import DeferredFormikTextField from "./deferredFormikTextField";
import TrasladosPuntosGate from "./trasladosPuntosGate";

const TrasladoCabeceraExtras = ({
  disabled,
  showArtOnlyFields,
  tiposTraslado,
  modalidades,
  tiposSolicitante,
  esPrestador,
  isRepeatForm = false,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const modalidadLocked =
    disabled || (!isRepeatForm && isModalidadRecorridoLocked(values?.puntos));

  const idEnElDia = useMemo(() => {
    const found = tiposTraslado.find((t) =>
      t.nombre?.toLowerCase().includes("día"),
    );
    return found?.id;
  }, [tiposTraslado]);

  const idPrestadorMedico = useMemo(() => {
    const found = tiposSolicitante.find((t) =>
      t.nombre?.toLowerCase().includes("prestador"),
    );
    return found?.id;
  }, [tiposSolicitante]);

  const isFechaHoy = useMemo(() => {
    if (!values.fechaHoraTurno) return false;
    return moment(values.fechaHoraTurno).isSame(moment(), "day");
  }, [values.fechaHoraTurno]);

  const isFechaFutura = useMemo(() => {
    if (!values.fechaHoraTurno) return false;
    return moment(values.fechaHoraTurno).isAfter(moment(), "day");
  }, [values.fechaHoraTurno]);

  // Punto 2: Tipo solicitante fijo en Prestador Médico cuando ingresa un prestador
  useEffect(() => {
    if (esPrestador && idPrestadorMedico) {
      if (String(values.idTipoSolicitante) !== String(idPrestadorMedico)) {
        setFieldValue("idTipoSolicitante", idPrestadorMedico);
      }
    }
  }, [esPrestador, idPrestadorMedico, values.idTipoSolicitante, setFieldValue]);

  // Punto 6: Si fecha es hoy -> En el día. Si es futura -> no puede ser En el día.
  useEffect(() => {
    if (!idEnElDia) return;
    if (isFechaHoy) {
      if (String(values.idTipoTraslado) !== String(idEnElDia)) {
        setFieldValue("idTipoTraslado", idEnElDia);
      }
    } else if (isFechaFutura && String(values.idTipoTraslado) === String(idEnElDia)) {
      setFieldValue("idTipoTraslado", "");
    }
  }, [isFechaHoy, isFechaFutura, idEnElDia, values.idTipoTraslado, setFieldValue]);

  const mappedTiposTraslado = useMemo(() => {
    return tiposTraslado.map((tipo) => {
      const isTipoEnElDia = Boolean(idEnElDia && String(tipo.id) === String(idEnElDia));
      let isDisabled = false;
      if (isFechaHoy && !isTipoEnElDia) {
        isDisabled = true;
      } else if (isFechaFutura && isTipoEnElDia) {
        isDisabled = true;
      }
      return {
        value: tipo.id,
        label: tipo.nombre ?? tipo.name ?? "",
        disabled: isDisabled,
      };
    });
  }, [tiposTraslado, idEnElDia, isFechaHoy, isFechaFutura]);
  const tiposTrasladoItems = useMemo(
    () => catalogSelectMenuItems(mappedTiposTraslado),
    [mappedTiposTraslado],
  );
  const modalidadItems = useMemo(
    () => catalogSelectMenuItems(mapCatalogToOptions(modalidades)),
    [modalidades],
  );
  const tiposSolicitanteItems = useMemo(
    () => catalogSelectMenuItems(mapCatalogToOptions(tiposSolicitante)),
    [tiposSolicitante],
  );
  const origenPedidoItems = useMemo(
    () => catalogSelectMenuItems(mapCatalogToOptions(ORIGENES_PEDIDO)),
    [],
  );

  return (
    <>
      <StyledCommonForms.RowContainer>
        <StyledCommonForms.LeftColumnContainer>
          <FormikSelect
            disabled={disabled || isFechaHoy}
            label="Tipo de traslado *"
            name="idTipoTraslado"
            sx={StyledCommonForms.input}
            fullWidth
            variant="outlined">
            {tiposTrasladoItems}
          </FormikSelect>
        </StyledCommonForms.LeftColumnContainer>
        <StyledCommonForms.RightColumnContainer>
          <FormikSelect
            disabled={modalidadLocked}
            helperText={
              modalidadLocked && !disabled
                ? "Hay tramos despachados o cerrados: la modalidad no se puede cambiar desde el formulario (CA18)."
                : undefined
            }
            label="Modalidad *"
            name="idModalidad"
            sx={StyledCommonForms.input}
            fullWidth
            variant="outlined">
            {modalidadItems}
          </FormikSelect>
        </StyledCommonForms.RightColumnContainer>
      </StyledCommonForms.RowContainer>
      <StyledCommonForms.RowContainer>
        <StyledCommonForms.LeftColumnContainer>
          <FormikSelect
            disabled={disabled || esPrestador}
            label="Tipo de solicitante *"
            name="idTipoSolicitante"
            sx={StyledCommonForms.input}
            fullWidth
            variant="outlined">
            {tiposSolicitanteItems}
          </FormikSelect>
        </StyledCommonForms.LeftColumnContainer>
        <StyledCommonForms.RightColumnContainer>
          {showArtOnlyFields ? (
            <FormikSelect
              disabled={disabled}
              label="Origen del pedido *"
              name="origenPedido"
              sx={StyledCommonForms.input}
              fullWidth
              variant="outlined">
              {origenPedidoItems}
            </FormikSelect>
          ) : (
            <DeferredFormikTextField
              disabled={disabled}
              inputProps={{ maxLength: 40 }}
              label="Solicitante"
              name="solicitadoPor"
              sx={StyledCommonForms.input}
              variant="outlined"
            />
          )}
        </StyledCommonForms.RightColumnContainer>
      </StyledCommonForms.RowContainer>
      {showArtOnlyFields && (
        <StyledCommonForms.RowContainer>
          <StyledCommonForms.LeftColumnContainer>
            <DeferredFormikTextField
              disabled={disabled}
              inputProps={{ maxLength: 40 }}
              label="Solicitante"
              name="solicitadoPor"
              sx={StyledCommonForms.input}
              variant="outlined"
            />
          </StyledCommonForms.LeftColumnContainer>
        </StyledCommonForms.RowContainer>
      )}
    </>
  );
};

const TrasladoPuntosSection = memo(
  ({
    puntos,
    puntosError,
    values,
    tramosDefaults,
    recorridoLocked,
    showArtOnlyFields,
    showMapPuntos,
    showMapTramos,
    fields,
    idArt,
    fechaHoraTurno,
    idEstadoInicial,
    tiposTransporte,
    motivosTraslado,
    agencias,
    prestadoresTraslado,
    setFieldValue,
    isRepeatForm = false,
  }) => (
    <>
      <RecorridoList
        disabled={recorridoLocked}
        fechaHoraTurno={fechaHoraTurno}
        fields={fields}
        idArt={idArt}
        idEstadoInicial={idEstadoInicial}
        isRepeatForm={isRepeatForm}
        puntos={puntos}
        setFieldValue={setFieldValue}
        showMap={showMapPuntos}
        tramosDefaults={tramosDefaults}
        values={values}
      />
      {puntosError && (
        <FormHelperText error sx={{ mt: -2, mb: 2, fontSize: "1rem" }}>
          {puntosError}
        </FormHelperText>
      )}

      <TramosAGenerarResumen puntos={puntos} />
      <TramosGestionList
        agencias={agencias}
        prestadoresTraslado={prestadoresTraslado}
        disabled={recorridoLocked}
        idEstadoInicial={idEstadoInicial}
        idArt={idArt}
        isRepeatForm={isRepeatForm}
        motivosTraslado={motivosTraslado}
        puntos={puntos}
        showAgencia={showArtOnlyFields}
        showMap={showMapTramos}
        tiposTransporte={tiposTransporte}
      />
    </>
  ),
  (prev, next) =>
    prev.puntos === next.puntos &&
    (prev.puntos?.length ?? 0) === (next.puntos?.length ?? 0) &&
    prev.tramosDefaults === next.tramosDefaults &&
    prev.recorridoLocked === next.recorridoLocked &&
    prev.showArtOnlyFields === next.showArtOnlyFields &&
    prev.showMapPuntos === next.showMapPuntos &&
    prev.showMapTramos === next.showMapTramos &&
    prev.fields === next.fields &&
    prev.idArt === next.idArt &&
    prev.fechaHoraTurno === next.fechaHoraTurno &&
    prev.idEstadoInicial === next.idEstadoInicial &&
    prev.tiposTransporte === next.tiposTransporte &&
    prev.motivosTraslado === next.motivosTraslado &&
    prev.agencias === next.agencias &&
    prev.prestadoresTraslado === next.prestadoresTraslado &&
    prev.isRepeatForm === next.isRepeatForm,
);

const TrasladoDirections = memo(
  ({
    disabled,
    showArtOnlyFields,
    showMapPuntos,
    showMapTramos,
    fields,
    idArt,
    idEstadoInicial,
    modalidades,
    tiposTransporte,
    motivosTraslado,
    agencias,
    prestadoresTraslado,
    esPrestador,
    isRepeatForm = false,
  }) => (
    <>
      <ModalidadRetornoSync
        disabled={disabled}
        idEstadoInicial={idEstadoInicial}
        isRepeatForm={isRepeatForm}
        modalidades={modalidades}
      />
      <TramoGestionDefaults
        agencias={agencias}
        prestadoresTraslado={prestadoresTraslado}
        disabled={disabled}
        idEstadoInicial={idEstadoInicial}
        motivosTraslado={motivosTraslado}
        showAgencia={showArtOnlyFields}
        tiposTransporte={tiposTransporte}
      />
      <TrasladosPuntosGate>
        {({
          fechaHoraTurno,
          puntos,
          puntosError,
          setFieldValue,
          tramosDefaults,
          values,
        }) => (
          <TrasladoPuntosSection
            agencias={agencias}
            prestadoresTraslado={prestadoresTraslado}
            fechaHoraTurno={fechaHoraTurno}
            fields={fields}
            idArt={idArt}
            idEstadoInicial={idEstadoInicial}
            isRepeatForm={isRepeatForm}
            key={puntos.map((punto) => punto.id || "").join("-") || puntos.length}
            motivosTraslado={motivosTraslado}
            puntos={puntos}
            puntosError={puntosError}
            recorridoLocked={
              disabled ||
              (!isRepeatForm && recorridoSoloLecturaPrestador(esPrestador, puntos))
            }
            setFieldValue={setFieldValue}
            showArtOnlyFields={showArtOnlyFields}
            showMapPuntos={showMapPuntos}
            showMapTramos={showMapTramos}
            tiposTransporte={tiposTransporte}
            tramosDefaults={tramosDefaults}
            values={values}
          />
        )}
      </TrasladosPuntosGate>
    </>
  ),
  (prev, next) =>
    prev.disabled === next.disabled &&
    prev.showArtOnlyFields === next.showArtOnlyFields &&
    prev.showMapPuntos === next.showMapPuntos &&
    prev.showMapTramos === next.showMapTramos &&
    prev.fields === next.fields &&
    prev.idArt === next.idArt &&
    prev.idEstadoInicial === next.idEstadoInicial &&
    prev.tiposTransporte === next.tiposTransporte &&
    prev.motivosTraslado === next.motivosTraslado &&
    prev.agencias === next.agencias &&
    prev.prestadoresTraslado === next.prestadoresTraslado &&
    prev.modalidades === next.modalidades &&
    prev.esPrestador === next.esPrestador &&
    prev.isRepeatForm === next.isRepeatForm,
);

const FptTraslados = (props) => {
  const esPrestador = useSelector((state) => state.auth.roles?.PRESTADOR);
  const showArtOnlyFields = !esPrestador;
  const config = props.model?.configuration || {};
  const showMapPuntos = esPrestador
    ? Boolean(config.showMapPrestador)
    : (config.showMapAT ?? true);
  const showMapTramos = showArtOnlyFields && (config.showMapTramosAT ?? true);
  const disabled =
    Boolean(props.disabled) || Boolean(esPrestador && props.isView);
  const fields = props.model.fields;
  const rawTiposTransporte = getFormFieldOptions(
    fields,
    "tipoTransporteNombre",
    "TipoTransporteNombre",
  );
  const tiposTransporte = useMemo(
    () =>
      rawTiposTransporte.filter(
        (t) =>
          String(t.id) !== "0" &&
          !String(t.nombre || "").toLowerCase().includes("sin transporte"),
      ),
    [rawTiposTransporte],
  );
  const agencias = getFormFieldOptions(fields, "idAgencia", "IdAgencia");
  const prestadoresTraslado = getFormFieldOptions(fields, "idPrestadorTraslado", "IdPrestadorTraslado");
  const motivosTraslado = getFormFieldOptions(
    fields,
    "idMotivoTraslado",
    "IdMotivoTraslado",
  );
  const modalidades = getFormFieldOptions(fields, "idModalidad", "IdModalidad");
  const tiposTraslado = getFormFieldOptions(
    fields,
    "idTipoTraslado",
    "IdTipoTraslado",
  );
  const tiposSolicitante = getFormFieldOptions(
    fields,
    "idTipoSolicitante",
    "IdTipoSolicitante",
  );
  const idEstadoInicial = getIdEstadoInicialTraslados(fields);

  const extraValidate = useCallback(
    (values) => {
      const errors =
        validatePuntosForm(
          values.puntos,
          values.fechaHoraTurno,
          showArtOnlyFields,
        ) || {};
      if (showArtOnlyFields && !values.origenPedido) {
        errors.origenPedido = "Campo requerido";
      }
      if (values.solicitadoPor && String(values.solicitadoPor).length > 40) {
        errors.solicitadoPor = "El campo no puede superar los 40 caracteres";
      }
      if (values.fechaHoraTurno && values.idTipoTraslado) {
        const isEnElDia = String(values.idTipoTraslado) === "1";
        const isHoy = moment(values.fechaHoraTurno).isSame(moment(), "day");
        const isFuturo = moment(values.fechaHoraTurno).isAfter(moment(), "day");

        if (isHoy && !isEnElDia) {
          errors.idTipoTraslado =
            "Si la fecha es hoy, el tipo de traslado debe ser 'En el día'.";
        }
        if (isEnElDia && isFuturo) {
          errors.fechaHoraTurno =
            "Para un traslado 'En el día', la fecha no puede ser posterior al día de hoy.";
        }
      }
      return errors;
    },
    [showArtOnlyFields],
  );

  const renderTrasladoExtras = useCallback(
    () => (
      <TrasladoCabeceraExtras
        disabled={disabled}
        esPrestador={esPrestador}
        isRepeatForm={props.isRepeatForm}
        modalidades={modalidades}
        showArtOnlyFields={showArtOnlyFields}
        tiposSolicitante={tiposSolicitante}
        tiposTraslado={tiposTraslado}
      />
    ),
    [
      disabled,
      esPrestador,
      props.isRepeatForm,
      modalidades,
      showArtOnlyFields,
      tiposSolicitante,
      tiposTraslado,
    ],
  );

  const renderDirections = useCallback(
    () => (
      <TrasladoDirections
        agencias={agencias}
        prestadoresTraslado={prestadoresTraslado}
        disabled={disabled}
        esPrestador={esPrestador}
        fields={fields}
        idArt={props.idArt}
        idEstadoInicial={idEstadoInicial}
        isRepeatForm={props.isRepeatForm}
        modalidades={modalidades}
        motivosTraslado={motivosTraslado}
        showArtOnlyFields={showArtOnlyFields}
        showMapPuntos={showMapPuntos}
        showMapTramos={showMapTramos}
        tiposTransporte={tiposTransporte}
      />
    ),
    [
      agencias,
      prestadoresTraslado,
      esPrestador,
      fields,
      idEstadoInicial,
      modalidades,
      motivosTraslado,
      disabled,
      props.idArt,
      props.isRepeatForm,
      showArtOnlyFields,
      showMapPuntos,
      showMapTramos,
      tiposTransporte,
    ],
  );

  return (
    <Fpt
      {...props}
      disabled={disabled}
      extraValidate={extraValidate}
      hideEsperaRetorno
      hideSolicitadoPor
      hideTipoTransporte
      isolateFormFields
      renderDirections={renderDirections}
      renderTrasladoExtras={renderTrasladoExtras}
      validateOnChange={false}
    />
  );
};

export default FptTraslados;
